/**
 * Impersonation storage.
 *
 * Two scopes:
 *
 *  - **localStorage** (`__IMPERSONATED_USER_ID__`): persistent, set by the in-app command palette in
 *    standalone RAM. Aligned with Cockpit's
 *    `services/core/cockpit/src/store/modules/generic.store.ts`.
 *  - **sessionStorage** (`__IMPERSONATED_USER_ID_SESSION__`): tab-scoped, set by the iframe-embed
 *    bootstrap (`@/shared/embed/impersonation-bootstrap.ts`) from a query param sent by Cockpit.
 *    Using sessionStorage prevents an iframe-injected impersonation from leaking into a separate
 *    standalone RAM tab on the same origin. The sentinel empty string means "explicitly cleared,
 *    override any stale localStorage value".
 *
 * `readEffectiveImpersonatedUserId` is the single read used by the HTTP client when deciding
 * whether to send the `x-impersonate-user` header: session scope (when present) wins over
 * localStorage.
 *
 * Return route (`__RAM_RETURN_ROUTE__`): path+search saved before in-RAM impersonation so
 * unimpersonate can restore the admin's previous page (mirrors Cockpit `__COCKPIT_RETURN_ROUTE__`).
 */
export const IMPERSONATED_USER_STORAGE_KEY = '__IMPERSONATED_USER_ID__';
export const IMPERSONATED_USER_SESSION_STORAGE_KEY = '__IMPERSONATED_USER_ID_SESSION__';
export const RAM_RETURN_ROUTE_STORAGE_KEY = '__RAM_RETURN_ROUTE__';

/** Default delay before reload/redirect so the impersonation toast is readable. */
export const IMPERSONATION_RELOAD_DELAY_MS = 2000;

export function readImpersonatedUserIdFromStorage(): string | null {
    try {
        return localStorage.getItem(IMPERSONATED_USER_STORAGE_KEY);
    } catch {
        return null;
    }
}

export function writeImpersonatedUserIdToStorage(userId: string | null): void {
    try {
        if (userId) {
            localStorage.setItem(IMPERSONATED_USER_STORAGE_KEY, userId);
        } else {
            localStorage.removeItem(IMPERSONATED_USER_STORAGE_KEY);
        }
    } catch (error) {
        console.warn('Failed to persist impersonated user id', error);
    }
}

/**
 * Read the iframe-injected (session-scoped) impersonation override.
 * - `undefined` → not set; fall back to localStorage.
 * - `null` → explicitly cleared by the embed; override localStorage.
 * - `string` → explicitly set by the embed.
 */
export function readSessionImpersonatedUserId(): string | null | undefined {
    try {
        const raw = sessionStorage.getItem(IMPERSONATED_USER_SESSION_STORAGE_KEY);
        if (raw === null) {
            return undefined;
        }
        return raw.length > 0 ? raw : null;
    } catch {
        return undefined;
    }
}

export function writeSessionImpersonatedUserId(userId: string | null): void {
    try {
        sessionStorage.setItem(IMPERSONATED_USER_SESSION_STORAGE_KEY, userId ?? '');
    } catch (error) {
        console.warn('Failed to persist session impersonated user id', error);
    }
}

export function clearSessionImpersonatedUserId(): void {
    try {
        sessionStorage.removeItem(IMPERSONATED_USER_SESSION_STORAGE_KEY);
    } catch {
        /* ignore */
    }
}

/** Resolve the effective impersonation: session override wins, otherwise localStorage. */
export function readEffectiveImpersonatedUserId(): string | null {
    const session = readSessionImpersonatedUserId();
    if (session !== undefined) {
        return session;
    }
    return readImpersonatedUserIdFromStorage();
}

export function writeRamReturnRoute(pathWithSearch: string): void {
    try {
        localStorage.setItem(RAM_RETURN_ROUTE_STORAGE_KEY, pathWithSearch);
    } catch (error) {
        console.warn('Failed to persist RAM return route', error);
    }
}

/** Read and clear the saved return route (one-shot, like Cockpit). */
export function consumeRamReturnRoute(): string | null {
    try {
        const route = localStorage.getItem(RAM_RETURN_ROUTE_STORAGE_KEY);
        if (route) {
            localStorage.removeItem(RAM_RETURN_ROUTE_STORAGE_KEY);
        }
        return route;
    } catch {
        return null;
    }
}

/**
 * Persist impersonation id and reload after a short delay (toast readable).
 */
export function beginImpersonationReload(userId: string, delayMs = IMPERSONATION_RELOAD_DELAY_MS): void {
    writeRamReturnRoute(`${window.location.pathname}${window.location.search}`);
    writeImpersonatedUserIdToStorage(userId);
    setTimeout(() => location.reload(), delayMs);
}

export function beginUnimpersonationReload(delayMs = IMPERSONATION_RELOAD_DELAY_MS): void {
    writeImpersonatedUserIdToStorage(null);
    setTimeout(() => location.reload(), delayMs);
}

/**
 * After unimpersonate reload: if a return route was saved and we are no longer
 * impersonating, navigate back to the admin's previous page.
 */
export function restoreRamReturnRouteIfNeeded(isImpersonating: boolean): void {
    if (isImpersonating) {
        return;
    }
    const returnRoute = consumeRamReturnRoute();
    if (!returnRoute) {
        return;
    }
    const current = `${window.location.pathname}${window.location.search}`;
    if (returnRoute === current) {
        return;
    }
    // Relative paths only — never open an absolute URL from storage.
    if (!returnRoute.startsWith('/') || returnRoute.startsWith('//')) {
        return;
    }
    location.replace(returnRoute);
}
